import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { BetSubmissionStatus } from '@/feature/betslip-sbk/types';
import {
    betSlipInitialTabSelector,
    comboOddsSelector,
    eventSelector,
    isOddsBetrBucksSelector,
    previousComboOddsSelector,
    selectionCountSelector,
    selectionSelector,
} from '@/feature/betslip-sbk/utils/betslip-selectors';
import { isComboSelectionEnabled, isConflictingSelection } from '@/feature/betslip-sbk/utils/betslip-utils';
import { Supplier } from '@/feature/event-details-sbk/types';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { useWallet } from '@/hooks/use-wallet';
import { isOptionSuspended, isProducerDown } from '@/utils/option-status';

export const useActiveSelectionCount = () => {
    return useSbkBetSlipStore(state => {
        return Object.values(state.selections).filter(
            selection => isComboSelectionEnabled(selection, state) && !isConflictingSelection(selection, state)
        ).length;
    });
};

export const useActiveEventCount = () =>
    useSbkBetSlipStore(state => {
        const activeSelections = Object.values(state.selections).filter(
            selection => isComboSelectionEnabled(selection, state) && !isConflictingSelection(selection, state)
        );
        const uniqueEventIds = new Set(activeSelections.map(s => s.eventId));
        return uniqueEventIds.size;
    });

export const useSelectionCount = () => useSbkBetSlipStore(selectionCountSelector);

export const useEvent = (eventId: string) => useSbkBetSlipStore(eventSelector(eventId));

export const useSelection = (selectionId: string) => useSbkBetSlipStore(selectionSelector(selectionId));

export const useIsBetSlipSubmitting = () =>
    useSbkBetSlipStore(state => state.betSubmissionStatus === BetSubmissionStatus.Submitting);

export const useIsOddsBetrBucks = (betId: string) => useSbkBetSlipStore(isOddsBetrBucksSelector(betId));

export const useComboOdds = () => useSbkBetSlipStore(comboOddsSelector);

export const usePreviousComboOdds = () => useSbkBetSlipStore(previousComboOddsSelector);

export const useIsOptionSuspended = (optionId: string) => {
    return useSbkBetSlipStore(state => {
        const option = state.options[optionId];
        const market = state.markets[option?.marketId];
        const optionStatus = option?.status ?? 'SUSPENDED';
        const producerDown = isProducerDown(state.producerStatuses, market?.supplier);
        return isOptionSuspended(optionStatus, producerDown, market?.published ?? false);
    });
};

export const useIsProducerDown = (supplier?: Supplier) =>
    useSbkBetSlipStore(state => isProducerDown(state.producerStatuses, supplier));

export const useBetSlipInitialTab = () => useSbkBetSlipStore(betSlipInitialTabSelector);

export const useShowBonusToggle = () => {
    const isPredictions = useIsPredictionsProduct();
    const isBetrCash = useSocialCurrencyStore(state => state.activeCurrency === SocialCurrency.CASH);
    const { betrBucks } = useWallet();
    return !isPredictions && isBetrCash && betrBucks > 0;
};
